import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiX, FiChevronRight, FiChevronLeft, FiCheck, FiLoader } from 'react-icons/fi';
import StepAudience from './wizard/StepAudience';
import StepModules from './wizard/StepModules';
import StepSchedule from './wizard/StepSchedule';
import StepReview from './wizard/StepReview';
import { dataFetch } from '../../api';

const STEPS = ['Audience', 'Modules', 'Schedule', 'Review'];

const getArray = (val) => {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      if (val.includes(',')) return val.split(',').map(s => s.trim());
      return [val];
    }
  }
  return [val];
};

const CreateCampaignModal = ({ onClose, onCreate, onUpdate, initialData }) => {
  const isEditing = !!initialData;
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  
  const [departmentsList, setDepartmentsList] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  
  const [modulesList, setModulesList] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);

  useEffect(() => {
    const fetchEmployees = async () => {
      setLoadingEmployees(true);
      try {
        const res = await dataFetch('read/employees?limit=5000');
        if (res?.status === 'success') {
          setEmployees(res.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch employees', error);
      }
      setLoadingEmployees(false);
    };

    const fetchDepartments = async () => {
      setLoadingDepartments(true);
      try {
        const res = await dataFetch('read/departments');
        if (res?.status === 'success') {
          setDepartmentsList(res.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch departments', error);
      }
      setLoadingDepartments(false);
    };

    const fetchModules = async () => {
      setLoadingModules(true);
      try {
        const res = await dataFetch('read/modules');
        if (res?.status === 'success') {
          setModulesList(res.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch modules', error);
      }
      setLoadingModules(false);
    };

    fetchEmployees();
    fetchDepartments();
    fetchModules();
  }, []);

  const [form, setForm] = useState(initialData ? {
    ...initialData,
    departments: initialData.audience && initialData.audience !== 'Custom' ? initialData.audience.split(',').map(s => s.trim()) : [],
    locations: [],
    roles: [],
    individualLearners: getArray(initialData.assignedLearners),
    trainingContent: getArray(initialData.trainingContent),
    reminderDays: [7, 3, 1],
    reminderEnabled: true,
  } : {
    campaignName: '',
    campaignType: 'Mandatory',
    audience: '',
    departments: [],
    locations: [],
    roles: [],
    individualLearners: [],
    trainingContent: [],
    startDate: '',
    endDate: '',
    reminderDays: [7, 3, 1],
    reminderEnabled: true,
  });

  const update = (patch) => setForm(f => ({ ...f, ...patch }));

  const handleCreateOrUpdate = async () => {
    setLoading(true);
    const userId = localStorage.getItem('user_id') || '';
    
    const payload = {
      campaignName: form.campaignName,
      campaignType: form.campaignType,
      audience: form.departments.length > 0 ? form.departments.join(', ') : 'Custom',
      assignedLearners: JSON.stringify(form.individualLearners || []),
      startDate: form.startDate,
      endDate: form.endDate,
      status: form.status || 'Active',
      completionRate: form.completionRate || 0,
      trainingContent: JSON.stringify(form.trainingContent || []),
      user_id: userId
    };

    try {
      let activeCampaignId = null;
      let finalCampaign = null;

      if (isEditing) {
        const res = await dataFetch(`update/campaigns/${initialData.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });

        if (res?.status === 'success') {
          activeCampaignId = initialData.id;
          finalCampaign = {
            ...initialData,
            ...payload,
            assignedLearners: form.individualLearners || [],
            trainingContent: form.trainingContent || []
          };

          // Delete existing reminders for this campaign before recreation
          const remRes = await dataFetch(`read/campaign_reminders?campaign_id=${activeCampaignId}&limit=1000`);
          if (remRes?.status === 'success' && remRes.data?.length > 0) {
             const idsToDelete = remRes.data.map(r => r.id);
             for (let i = 0; i < idsToDelete.length; i += 500) {
               await dataFetch('bulk/delete/campaign_reminders', {
                 method: 'DELETE',
                 body: JSON.stringify({ ids: idsToDelete.slice(i, i + 500) })
               });
             }
          }
        } else {
          console.error('Failed to update campaign', res);
          alert('Failed to update campaign');
        }
      } else {
        const res = await dataFetch('create/campaigns', {
          method: 'POST',
          body: JSON.stringify(payload)
        });

        if (res?.status === 'success') {
          activeCampaignId = res.data.id;
          finalCampaign = {
            ...res.data,
            assignedLearners: form.individualLearners || [],
            trainingContent: form.trainingContent || []
          };
        } else {
          console.error('Failed to create campaign', res);
          alert('Failed to create campaign');
        }
      }

      // Add corresponding reminder logs to campaign_reminders table
      if (activeCampaignId && finalCampaign) {
        if (form.reminderEnabled && form.reminderDays.length > 0) {
          const targetedEmployees = employees.filter(emp => {
            const inDept = form.departments.length > 0 && form.departments.includes(emp.department);
            const inIndividual = form.individualLearners.some(id => String(id) === String(emp.id));
            return inDept || inIndividual;
          });

          const reminderRecords = [];
          targetedEmployees.forEach(emp => {
            form.reminderDays.forEach(days => {
              reminderRecords.push({
                created_by_id: userId,
                employee_id: String(emp.id),
                campaign_id: String(activeCampaignId),
                reminder_days: Number(days),
                start_date: form.startDate,
                due_date: form.endDate,
                user_id: userId
              });
            });
          });

          if (reminderRecords.length > 0) {
            for (let i = 0; i < reminderRecords.length; i += 500) {
              await dataFetch('bulk/create/campaign_reminders', {
                method: 'POST',
                body: JSON.stringify({ records: reminderRecords.slice(i, i + 500) })
              });
            }
          }
        }

        if (isEditing) {
          if (onUpdate) onUpdate(finalCampaign);
        } else {
          if (onCreate) onCreate(finalCampaign);
        }
      }
    } catch (error) {
      console.error('Failed to save campaign', error);
      alert('Failed to save campaign');
    }
    setLoading(false);
  };

  const canNext = () => {
    if (step === 0) return form.campaignName.trim().length > 0;
    if (step === 1) return form.trainingContent.length > 0;
    if (step === 2) return form.startDate && form.endDate;
    return true;
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <motion.div initial={{ opacity: 0, scale: 0.96, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col z-10">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h2 className="text-base font-semibold text-slate-800">{isEditing ? 'Edit Campaign' : 'Create Training Campaign'}</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100">
              <SafeIcon icon={FiX} />
            </button>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center gap-0 px-6 py-4 border-b border-slate-100">
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-2 text-xs font-semibold transition-colors ${i === step ? 'text-blue-600' : i < step ? 'text-emerald-600' : 'text-slate-400'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${i === step ? 'bg-blue-600 text-white' : i < step ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                    {i < step ? <SafeIcon icon={FiCheck} /> : i + 1}
                  </div>
                  <span className="hidden sm:block">{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i < step ? 'bg-emerald-400' : 'bg-slate-200'}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {step === 0 && (
              <StepAudience 
                form={form} 
                update={update} 
                employees={employees} 
                loading={loadingEmployees} 
                departmentsList={departmentsList}
                loadingDepartments={loadingDepartments}
              />
            )}
            {step === 1 && (
              <StepModules 
                form={form} 
                update={update} 
                modules={modulesList} 
                loading={loadingModules} 
              />
            )}
            {step === 2 && <StepSchedule form={form} update={update} />}
            {step === 3 && <StepReview form={form} employees={employees} />}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
            <button onClick={() => step > 0 ? setStep(s => s - 1) : onClose()}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              <SafeIcon icon={FiChevronLeft} /> {step === 0 ? 'Cancel' : 'Back'}
            </button>
            <button
              onClick={() => step < STEPS.length - 1 ? setStep(s => s + 1) : handleCreateOrUpdate()}
              disabled={!canNext() || loading}
              className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg transition-colors ${(canNext() && !loading) ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}>
              {loading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : null}
              {step === STEPS.length - 1 ? (loading ? (isEditing ? 'Saving...' : 'Creating...') : (isEditing ? 'Save Changes' : 'Create Campaign')) : 'Next'} <SafeIcon icon={step === STEPS.length - 1 ? FiCheck : FiChevronRight} />
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CreateCampaignModal;